import { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons/faGripDotsVertical';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { emptyArray } from 'ui-components/default-props/empty';
import UserDateFormat from '../../../../components/user-date-format/index';
import { AutomationRulesContext, doesUserHaveAccessToEveryActiveRuleAction } from '../context';
import ActionMenu from './action-menu/action-menu';
import OpenDeleteModalButton from './action-menu/open-delete-modal-button';
import { RuleCopyLink } from './action-menu/rule-copy-link';
import { RuleEditorLink } from './action-menu/rule-editor-link';
import { FilterEditorContext } from '../../../../components/filter-editor/context';

import messages from '../messages';
import style from './style.module.less';

/** @typedef {import('../context').AutomationRule} AutomationRule */

const MINIMUM_DRAG_DISTANCE = 8;

const gridHeader = (
  <div className={style['grid-header']}>
    <div className={style['header-item']}>&nbsp;</div>
    <div className={style['header-item']}>
      <FormattedMessage {...messages.ruleName} />
    </div>
    <div className={style['header-item']}>
      <FormattedMessage {...messages.lastModified} />
    </div>
    <div className={classnames(style['header-item'], style['active-header'])}>
      <FormattedMessage {...messages.active} />
    </div>
    <div className={style['header-item']}>
      <FormattedMessage {...messages.actions} />
    </div>
  </div>
);

/**
 * @param {{ rule: AutomationRule, hasError: boolean }} props
 * @returns {JSX.Element}
 */
const SortableRule = ({ rule, hasError }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.automationRuleId,
  });
  const { toggleRuleActive } = useContext(AutomationRulesContext);

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleToggleActive = useCallback(
    (newActive) => {
      toggleRuleActive(rule, newActive);
    },
    [toggleRuleActive, rule],
  );

  return (
    <div
      ref={setNodeRef}
      className={classnames(style['grid-row'], { [style.flying]: isDragging })}
      style={sortableStyle}
      {...attributes}
      {...listeners}
    >
      <div className={style['grid-item']} role='gridcell'>
        <Tooltip>
          <FontAwesomeIcon icon={faGripDotsVertical} />
          <TooltipContent>
            <FormattedMessage {...messages.dragToReorder} />
          </TooltipContent>
        </Tooltip>
      </div>
      <div className={style['grid-item']} role='gridcell'>
        {rule.name}
      </div>
      <div className={style['grid-item']}>
        <UserDateFormat value={rule.modifiedDate} />
      </div>
      <div className={style.activity}>
        {hasError ? (
          <FontAwesomeIcon className={style.error} icon={faExclamationCircle} />
        ) : (
          <CheckboxSlider checkboxId={rule.automationRuleId} checked={rule.active} onChange={handleToggleActive} />
        )}
      </div>
      <div className={style.actions}>
        <ActionMenu>
          <RuleEditorLink automationRuleId={rule.automationRuleId} />
          <RuleCopyLink automationRuleId={rule.automationRuleId} />
          <OpenDeleteModalButton automationRuleId={rule.automationRuleId} />
        </ActionMenu>
      </div>
    </div>
  );
};

/**
 * @returns {JSX.Element}
 */
const RulesGrid = () => {
  const { automationRules = emptyArray, onDragEnd, ruleTypesHashMap } = useContext(AutomationRulesContext);
  const { isLoadingFilters } = useContext(FilterEditorContext);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: MINIMUM_DRAG_DISTANCE } }));

  const itemIds = automationRules.map((rule) => rule.automationRuleId);

  const hasRuleError = (rule) => {
    if (!rule?.orderRuleActions || !ruleTypesHashMap) {
      return false;
    }
    return !doesUserHaveAccessToEveryActiveRuleAction(rule.orderRuleActions, ruleTypesHashMap);
  };

  return (
    <FlexColumn className={style['grid-container']}>
      {gridHeader}
      {isLoadingFilters && <LoadingIndicator className={style.loading} theme='grey' />}
      {!isLoadingFilters && (
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} sensors={sensors}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <div className={style['rules-droppable-wrap']}>
              {automationRules.map((rule) => (
                <SortableRule key={rule.automationRuleId} hasError={hasRuleError(rule)} rule={rule} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </FlexColumn>
  );
};

export default RulesGrid;
